import { fas } from '@awesome.me/kit-b4fd369ea4/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd';
import { Box, Button, Chip, IconButton, InputBase, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import {
  displayValueToken,
  formatFormulaDisplay,
  newOperatorToken,
  newVariableToken,
  normalizeValueToken,
  serializeTokens,
  TIER_FORMULA_OPERATORS,
  tokenizeFormula,
} from './TierFormulaUtils';

// A palette chip is dragged with the native HTML5 API (the palettes are immutable, so they aren't RBD Draggables
// that get removed while dragging); the payload identifies what to append to the formula it's dropped on
const VARIABLE_DRAG_PREFIX = 'variable:';
const OPERATOR_DRAG_PREFIX = 'operator:';

const MAIN_FORMULA = 'formula';
const THRESHOLD_FORMULA = 'threshold';

/**
 * Owns the editable token list for one formula string and keeps the two in sync: the string is tokenized on the way
 * in (and re-tokenized when it changes externally, ex: the entity finishing loading), and every token change is
 * serialized back out through onChange. Returned mutators are all the builder needs to edit the formula.
 */
const useFormulaTokens = ({ formula, onChange, codeToVariable, valueFormat }) => {
  const [tokens, setTokens] = useState(() => tokenizeFormula(formula, codeToVariable));

  // The last string this hook itself emitted, so its own onChange echoing back isn't mistaken for an external change
  const lastEmitted = useRef(formula);
  useEffect(() => {
    if (formula === lastEmitted.current) return;
    lastEmitted.current = formula;
    setTokens(tokenizeFormula(formula, codeToVariable));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formula]);

  // Variables load asynchronously; once they're in, backfill display names onto tokens parsed before that
  useEffect(() => {
    setTokens((prev) =>
      prev.map((token) => {
        const variable = token.type === 'variable' ? codeToVariable[token.code] : null;
        return variable ? { ...token, displayName: variable.tokenLabel ?? variable.displayName } : token;
      }),
    );
  }, [codeToVariable]);

  useEffect(() => {
    const serialized = serializeTokens(tokens);
    lastEmitted.current = serialized;
    onChange(serialized);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokens]);

  // New tokens always land at the end (typed values are normalized against whatever they now sit next to, ex:
  // "$500" -> "500"); the formula is then arranged by dragging tokens around within it
  const append = (newTokens) => {
    if (newTokens.length === 0) return;
    setTokens((prev) => {
      const next = [...prev, ...newTokens];
      return next.map((_token, i) => normalizeValueToken(next, i, codeToVariable, valueFormat));
    });
  };

  const removeAt = (index) => setTokens((prev) => prev.filter((_token, i) => i !== index));

  const move = (from, to) =>
    setTokens((prev) => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });

  return { tokens, append, removeAt, move };
};

// The bordered box a formula is edited in: its tokens as reorderable chips, followed by a free-text input for typing
// values directly. Palette chips dropped onto it (native drag) are appended. `locked` renders it read-only.
const FormulaTokenBox = ({ droppableId, editor, codeToVariable, valueFormat, locked, error, inputRef }) => {
  const [textInput, setTextInput] = useState('');
  const [isDragOver, setIsDragOver] = useState(false);
  const { tokens } = editor;

  const commitTextInput = () => {
    if (textInput.trim()) editor.append(tokenizeFormula(textInput.trim(), codeToVariable));
    setTextInput('');
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragOver(false);
    const payload = event.dataTransfer.getData('text/plain');
    if (payload.startsWith(VARIABLE_DRAG_PREFIX)) {
      const variable = codeToVariable[payload.slice(VARIABLE_DRAG_PREFIX.length)];
      if (variable) editor.append([newVariableToken(variable)]);
    } else if (payload.startsWith(OPERATOR_DRAG_PREFIX)) {
      editor.append([newOperatorToken(payload.slice(OPERATOR_DRAG_PREFIX.length))]);
    }
  };

  return (
    <Droppable droppableId={droppableId} direction="horizontal" isDropDisabled={locked}>
      {(provided, snapshot) => (
        <Box
          ref={provided.innerRef}
          {...provided.droppableProps}
          onClick={locked ? undefined : () => inputRef.current?.focus()}
          onDragOver={(event) => event.preventDefault()}
          onDragEnter={locked ? undefined : () => setIsDragOver(true)}
          onDragLeave={locked ? undefined : () => setIsDragOver(false)}
          onDrop={locked ? undefined : handleDrop}
          sx={(theme) => ({
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 1,
            minHeight: 56,
            px: 1.75,
            py: 1,
            cursor: locked ? 'default' : 'text',
            border: '1px solid',
            borderColor:
              error ? 'error.main'
              : !locked && (snapshot.isDraggingOver || isDragOver) ? 'primary.main'
              : theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.23)'
              : 'rgba(0, 0, 0, 0.23)',
            borderRadius: 1,
            bgcolor: locked ? 'action.disabledBackground' : undefined,
            '&:hover': { borderColor: locked ? undefined : error ? 'error.main' : 'text.primary' },
          })}
        >
          {tokens.map((token, index) => (
            <Draggable key={token.id} draggableId={token.id} index={index} isDragDisabled={locked}>
              {(provided, snapshot) => (
                <Chip
                  ref={provided.innerRef}
                  {...provided.draggableProps}
                  {...provided.dragHandleProps}
                  label={displayValueToken(tokens, index, codeToVariable, valueFormat)}
                  color={token.type === 'variable' ? 'primary' : 'default'}
                  variant={token.type === 'variable' ? 'filled' : 'outlined'}
                  onDelete={locked ? undefined : () => editor.removeAt(index)}
                  sx={{
                    cursor: locked ? 'default' : 'grab',
                    boxShadow: snapshot.isDragging ? 3 : 0,
                    // Delete icon only appears on hover, so chips read as formula tokens rather than removable tags
                    '& .MuiChip-deleteIcon': {
                      fontSize: 16,
                      width: 0,
                      opacity: 0,
                      margin: 0,
                      overflow: 'hidden',
                      transition: 'width 0.1s, opacity 0.1s, margin 0.1s',
                    },
                    '&:hover .MuiChip-deleteIcon': { width: 16, opacity: 1, margin: '0 4px 0 -2px' },
                  }}
                />
              )}
            </Draggable>
          ))}
          {provided.placeholder}
          {!locked && (
            <InputBase
              inputRef={inputRef}
              value={textInput}
              onChange={(event) => setTextInput(event.target.value)}
              onBlur={commitTextInput}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  commitTextInput();
                } else if (event.key === 'Backspace' && textInput === '' && tokens.length > 0) {
                  editor.removeAt(tokens.length - 1);
                }
              }}
              placeholder={tokens.length === 0 ? 'Type a number, or drag operators/variables in' : ''}
              sx={{ flex: '1 1 120px', minWidth: 120 }}
            />
          )}
        </Box>
      )}
    </Droppable>
  );
};

// A chip in the Operators/Variables palettes: click to append, or drag onto a formula box. The browser's default
// drag image is a translucent screenshot around the chip (selected text and anything behind the dialog bleed
// through), so an opaque clone of just the chip is used instead; it must sit inside the viewport - Chrome falls back
// to the screenshot for off-screen elements - so it's parked behind everything at the top-left corner.
const PaletteChip = ({ label, dragPayload, onClick, onDragStart }) => {
  const handleDragStart = (event) => {
    onDragStart();
    event.dataTransfer.setData('text/plain', dragPayload);

    window.getSelection()?.removeAllRanges();
    const chip = event.currentTarget;
    const surface = chip.closest('.MuiPaper-root');
    const ghost = chip.cloneNode(true);
    Object.assign(ghost.style, {
      position: 'fixed',
      top: 0,
      left: 0,
      zIndex: -1,
      pointerEvents: 'none',
      // Copies the dialog surface including the elevation overlay MUI layers on in dark mode
      backgroundColor: surface ? getComputedStyle(surface).backgroundColor : undefined,
      backgroundImage: surface ? getComputedStyle(surface).backgroundImage : undefined,
    });
    document.body.appendChild(ghost);
    event.dataTransfer.setDragImage(ghost, ghost.offsetWidth / 2, ghost.offsetHeight / 2);
    requestAnimationFrame(() => ghost.remove());
  };

  return (
    <Chip draggable onDragStart={handleDragStart} onClick={onClick} label={label} variant="outlined" sx={{ cursor: 'grab' }} />
  );
};

/**
 * Lets the user build a Tier formula by dragging Variables/operators in (or clicking to append them), reordering the
 * tokens by dragging them within the formula, and dragging a token out to delete it. Purely presentational/stateful -
 * no Dialog/submit chrome, so it can be embedded inline in a form field or wrapped in a standalone dialog.
 *
 * With `threshold` ({ formula, onChange, valueFormat }) the Tier also gets a Threshold: a second formula edited in
 * its own box below the (then locked) main one, and once saved offered as a [THRESHOLD] token for the main formula.
 */
export const TierFormulaBuilder = ({ formula, onChange, palette, label = 'Formula', error, disabled, threshold }) => {
  const { groupedVariables, codeToVariable } = palette;
  const inputRef = useRef(null);

  // The Threshold is drafted locally and only written back through threshold.onChange on Save
  const [thresholdDraft, setThresholdDraft] = useState(null);
  const editingThreshold = thresholdDraft !== null;

  const mainEditor = useFormulaTokens({ formula, onChange, codeToVariable });
  // Holds the saved Threshold (for read-only display) until a draft takes over
  const thresholdEditor = useFormulaTokens({
    formula: thresholdDraft ?? threshold?.formula ?? '',
    onChange: (draft) => editingThreshold && setThresholdDraft(draft),
    codeToVariable,
    valueFormat: threshold?.valueFormat,
  });
  const activeEditor = editingThreshold ? thresholdEditor : mainEditor;
  const editors = { [MAIN_FORMULA]: mainEditor, [THRESHOLD_FORMULA]: thresholdEditor };

  // Anything still typed in the text input is committed before a drag/click adds the next token after it
  const flushTextInput = () => inputRef.current?.blur();

  const handleTokenDragEnd = ({ source, destination }) => {
    const editor = editors[source.droppableId];
    if (!destination) editor.removeAt(source.index);
    else editor.move(source.index, destination.index);
  };

  const saveThreshold = () => {
    threshold.onChange(thresholdDraft);
    setThresholdDraft(null);
  };

  const paletteChip = (label, dragPayload, newToken) => (
    <PaletteChip
      key={dragPayload}
      label={label}
      dragPayload={dragPayload}
      onDragStart={flushTextInput}
      onClick={() => {
        flushTextInput();
        activeEditor.append([newToken()]);
      }}
    />
  );

  return (
    <DragDropContext onDragStart={flushTextInput} onDragEnd={handleTokenDragEnd}>
      <Typography variant="caption" color={error ? 'error' : 'text.secondary'} sx={{ display: 'block', mb: 0.5 }}>
        {label}
      </Typography>
      <FormulaTokenBox
        droppableId={MAIN_FORMULA}
        editor={mainEditor}
        codeToVariable={codeToVariable}
        locked={disabled || editingThreshold}
        error={error}
        inputRef={inputRef}
      />
      {error && (
        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
          {error}
        </Typography>
      )}

      {threshold && (!disabled || threshold.formula) && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: disabled ? 0.5 : 0 }}>
            Threshold
          </Typography>
          {!disabled && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              The threshold for incremental Net Sales, above which the discount applies.
            </Typography>
          )}
          {disabled || editingThreshold ?
            <>
              <FormulaTokenBox
                droppableId={THRESHOLD_FORMULA}
                editor={thresholdEditor}
                codeToVariable={codeToVariable}
                valueFormat={threshold.valueFormat}
                locked={disabled}
                inputRef={inputRef}
              />
              {!disabled && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                  <Button size="small" variant="contained" onClick={saveThreshold}>
                    Save
                  </Button>
                </Box>
              )}
            </>
          : threshold.formula ?
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {paletteChip(
                formatFormulaDisplay(threshold.formula, codeToVariable, threshold.valueFormat),
                `${VARIABLE_DRAG_PREFIX}THRESHOLD`,
                () => newVariableToken(codeToVariable.THRESHOLD ?? { code: 'THRESHOLD' }),
              )}
              <IconButton size="small" onClick={() => setThresholdDraft(threshold.formula)}>
                <FontAwesomeIcon icon={fas.faPencil} />
              </IconButton>
            </Box>
          : <Button size="small" startIcon={<FontAwesomeIcon icon={fas.faPlus} />} onClick={() => setThresholdDraft('')}>
              Add Threshold
            </Button>
          }
        </Box>
      )}

      {!disabled && (
        <>
          <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
            Operators
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {TIER_FORMULA_OPERATORS.map((operator) =>
              paletteChip(operator, `${OPERATOR_DRAG_PREFIX}${operator}`, () => newOperatorToken(operator)),
            )}
          </Box>

          <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
            Variables
          </Typography>
          {groupedVariables.size === 0 && (
            <Typography variant="body2" color="text.secondary">
              No Tier variables available for this Program
            </Typography>
          )}
          <Box sx={{ maxHeight: 320, overflowY: 'auto', pr: 0.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {[...groupedVariables].map(([groupName, groupVariables]) => (
              <Box key={groupName}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  {groupName}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.75 }}>
                  {groupVariables.map((variable) =>
                    paletteChip(
                      variable.displayName,
                      `${VARIABLE_DRAG_PREFIX}${variable.code}`,
                      () => newVariableToken(variable),
                    ),
                  )}
                </Box>
              </Box>
            ))}
          </Box>
        </>
      )}
    </DragDropContext>
  );
};
